import { ApplicationShell, FrontendApplication, FrontendApplicationContribution } from '@theia/core/lib/browser';
import {
    BrowserMainMenuFactory,
    DynamicMenuBarWidget,
    MenuBarWidget
} from '@theia/core/lib/browser/menu/browser-menu-plugin';
import { ContextKey, ContextKeyService } from '@theia/core/lib/browser/context-key-service';
import { inject, injectable } from '@theia/core/shared/inversify';

/** Theia context key (boolean): true only after {@link AuthStoreState.login} (`isAuthenticated === true`). */
export const AUTH_LOGGED_IN_CONTEXT_KEY = 'lvedLoggedIn';

/**
 * Mirrors @lved/ui auth into Theia {@link ContextKeyService} so menu `when` clauses update
 * (signed-in vs signed-out labels and Log out visibility).
 */
@injectable()
export class AuthContextContribution implements FrontendApplicationContribution {
    @inject(ContextKeyService)
    protected readonly contextKeys!: ContextKeyService;

    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    @inject(BrowserMainMenuFactory)
    protected readonly menuFactory!: BrowserMainMenuFactory & {
        setMenuBar?: () => void;
        refreshMenuBar: (menuBar: DynamicMenuBarWidget) => void;
    };

    async onStart(_app: FrontendApplication): Promise<void> {
        const key: ContextKey<boolean> = this.contextKeys.createKey<boolean>(AUTH_LOGGED_IN_CONTEXT_KEY, false);
        try {
            const { useAuthStore } = await import('@lved/ui');
            const sync = (): void => {
                key.set(useAuthStore.getState().isAuthenticated === true);
                this.refreshMenuBars();
            };
            sync();
            useAuthStore.subscribe(sync);
        } catch {
            key.set(false);
            this.refreshMenuBars();
        }
    }

    protected refreshMenuBars(): void {
        this.menuFactory.setMenuBar?.();
        const menuBar: DynamicMenuBarWidget | undefined = this.shell.topPanel.widgets.find(
            (widget): widget is DynamicMenuBarWidget => widget instanceof MenuBarWidget
        );
        if (menuBar) this.menuFactory.refreshMenuBar(menuBar);
    }
}
